const REQUEST_TIMEOUT_MS = 30000
const INIT_TIMEOUT_MS = 5000

/**
 * Singleton iframe-based auth proxy for cross-origin authentication requests.
 * Uses postMessage to communicate with an iframe hosted on the identity service
 * that can make requests with HttpOnly cookies.
 */
class IframeAuthProxy {
  constructor() {
    this.iframe = null
    this.ready = false
    this.pendingRequests = new Map()
    this.requestId = 0
    this.readyPromise = null
    this.initialized = false
    this.messageHandler = null
  }

  init() {
    if (this.initialized) {
      return this.readyPromise
    }
    this.initialized = true

    this.readyPromise = new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        reject(new Error('Auth proxy initialization timeout'))
      }, INIT_TIMEOUT_MS)

      this.iframe = document.createElement('iframe')
      this.iframe.src = `${IDENTITY_SERVICE_URL}/lender/auth/frame`
      this.iframe.style.display = 'none'

      this.messageHandler = (event) => {
        if (event.origin !== window.location.origin &&
          event.origin !== IDENTITY_SERVICE_ORIGIN) {
          return
        }

        if (event.data.type === 'proxy-ready') {
          clearTimeout(timeoutId)
          this.ready = true
          resolve()
          return
        }

        const { id, success, status, data, error } = event.data
        const pending = this.pendingRequests.get(id)

        if (pending) {
          if (success) {
            pending.resolve({ status, data })
          } else {
            pending.reject(new Error(error))
          }
          this.pendingRequests.delete(id)
        }
      }

      window.addEventListener('message', this.messageHandler)

      this.iframe.onerror = () => {
        clearTimeout(timeoutId)
        reject(new Error('Auth proxy iframe failed to load'))
      }

      this.iframe.onload = () => {
        // Iframe loaded, send a ping to request ready confirmation
        this.iframe.contentWindow.postMessage({ type: 'ping' }, IDENTITY_SERVICE_ORIGIN)
      }

      document.body.appendChild(this.iframe)
    })

    return this.readyPromise
  }

  async request(url, options = {}) {
    // Initialize if not already done
    if (!this.initialized) {
      this.init()
    }

    // Wait for proxy to be ready
    await this.readyPromise

    const id = this.requestId++

    return new Promise((resolve, reject) => {
      this.pendingRequests.set(id, { resolve, reject })

      // Send request to the iframe - it will make the actual HTTP call to the identity service
      this.iframe.contentWindow.postMessage({
        id,
        method: options.method || 'GET',
        url,
        body: options.body,
        headers: options.headers,
      }, IDENTITY_SERVICE_ORIGIN)

      setTimeout(() => {
        if (this.pendingRequests.has(id)) {
          this.pendingRequests.delete(id)
          reject(new Error('Request timeout'))
        }
      }, REQUEST_TIMEOUT_MS)
    })
  }

  async get(url, headers = {}) {
    const { data } = await this.request(url, { method: 'GET', headers })
    return data
  }

  async post(url, body = {}, headers = {}) {
    const { data } = await this.request(url, { method: 'POST', body, headers })
    return data
  }

  async postForm(url, formData, headers = {}) {
    // Convert FormData to plain object for postMessage serialization
    const body = {}
    for (const [key, value] of formData.entries()) {
      body[key] = value
    }
    const { data } = await this.request(url, {
      method: 'POST',
      body,
      headers: { ...headers, 'Content-Type': 'application/x-www-form-urlencoded' },
    })
    return data
  }
}

// Singleton instance
export const iframeAuthProxy = new IframeAuthProxy()

export default iframeAuthProxy
